import type { QueryClient } from 'react-query';

import minutesToMilliseconds from 'date-fns/minutesToMilliseconds';

import { fetchFreshMe, fetchMe } from '@api/user';
import { QUERY_KEYS } from '@constants';
import { IUserMe } from '@model';

export const CURRENT_USER_STALE_TIME = minutesToMilliseconds(60);
export const CURRENT_USER_CACHE_TIME = minutesToMilliseconds(60);

export const refreshCurrentUserAfterPayment = async (
  queryClient: QueryClient,
  accessToken?: string
) => {
  const currentUser = await fetchFreshMe(accessToken);
  queryClient.setQueryData<IUserMe>(QUERY_KEYS.USER.ME, currentUser);
  return currentUser;
};

export const markCurrentUserPaymentPending = (queryClient: QueryClient) =>
  queryClient.invalidateQueries(QUERY_KEYS.USER.ME, {
    refetchActive: false,
    refetchInactive: false,
  });

export type PaymentBalanceReconciliationResult = 'credited' | 'pending';

type PaymentBalanceReconciliationOptions = {
  queryClient: QueryClient;
  confirmPayment?: () => Promise<unknown>;
  onFinalFailure: (error: unknown) => void;
  accessToken?: string;
};

const PAYMENT_RECONCILIATION_RETRY_DELAYS = [0, 300, 1000, 2000];

const wait = (milliseconds: number) =>
  new Promise<void>((resolve) => setTimeout(resolve, milliseconds));

export const reconcileCurrentUserAfterPayment = async ({
  queryClient,
  confirmPayment,
  onFinalFailure,
  accessToken,
}: PaymentBalanceReconciliationOptions): Promise<PaymentBalanceReconciliationResult> => {
  let lastError: unknown;

  for (const delay of PAYMENT_RECONCILIATION_RETRY_DELAYS) {
    if (delay > 0) {
      await wait(delay);
    }

    try {
      await confirmPayment?.();
      await refreshCurrentUserAfterPayment(queryClient, accessToken);
      return 'credited';
    } catch (error) {
      lastError = error;
    }
  }

  onFinalFailure(lastError);
  await markCurrentUserPaymentPending(queryClient);
  return 'pending';
};

export const currentUserPrefetcher = {
  prefetch(queryClient: QueryClient, accessToken: string) {
    return queryClient.prefetchQuery<IUserMe>(
      QUERY_KEYS.USER.ME,
      () => fetchMe(accessToken),
      {
        staleTime: CURRENT_USER_STALE_TIME,
        cacheTime: CURRENT_USER_CACHE_TIME,
      }
    );
  },
};
